import React, { useState } from 'react';
import {
  Bell,
  Mail,
  Users,
  GraduationCap,
  Briefcase,
  MessageSquare,
  Calendar,
  Activity,
  Globe,
} from 'lucide-react';

interface NotificationSetting {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

export function NotificationsSection() {
  const [language, setLanguage] = useState('en');
  const [smtpLanguage, setSmtpLanguage] = useState('en');
  const [muteAll, setMuteAll] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSetting[]>([
    { id: 'mail', label: 'Mute Mail Notifications', description: 'Mute all email notifications sent to your inbox', icon: <Mail className="w-5 h-5" />, enabled: false },
    { id: 'students', label: 'Mute Students Notifications', description: 'Mute notifications related to student activities', icon: <GraduationCap className="w-5 h-5" />, enabled: false },
    { id: 'teachers', label: 'Mute Teachers Notifications', description: 'Mute notifications related to teacher activities', icon: <Users className="w-5 h-5" />, enabled: false },
    { id: 'project', label: 'Mute Project Notifications', description: 'Mute notifications about project updates', icon: <Briefcase className="w-5 h-5" />, enabled: false },
    { id: 'chat', label: 'Mute Group Chat Notifications', description: 'Mute notifications from group chats', icon: <MessageSquare className="w-5 h-5" />, enabled: false },
    { id: 'events', label: 'Mute Event Reminders', description: 'Mute reminders about upcoming events', icon: <Calendar className="w-5 h-5" />, enabled: false },
    { id: 'activity', label: 'Mute Activity Updates', description: 'Mute notifications about system activity', icon: <Activity className="w-5 h-5" />, enabled: false },
  ]);

  const handleMuteAllToggle = () => {
    const newMuteAll = !muteAll;
    setMuteAll(newMuteAll);
    if (newMuteAll) {
      setNotifications((prev) => prev.map((n) => ({ ...n, enabled: true })));
    }
  };

  const handleToggle = (id: string) => {
    if (muteAll) return; // Can't toggle individual when mute all is on
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, enabled: !n.enabled } : n))
    );
  };

  return (
    <div className="space-y-6">
      {/* Language Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">General Settings</h2>
        <p className="text-sm text-gray-500 mb-4">
          Set up your system as you like it. From here, you can change your language.
        </p>

        <div className="space-y-4">
          {/* UI Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Language
              </div>
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  language === 'en'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('he')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  language === 'he'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Hebrew
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This changes the system's display language.
            </p>
          </div>

          {/* SMTP Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                SMTP
              </div>
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSmtpLanguage('en')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  smtpLanguage === 'en'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setSmtpLanguage('he')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  smtpLanguage === 'he'
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Hebrew
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This changes the language for all automated emails (invitations, notifications, confirmations).
            </p>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h2>

        {/* Mute All Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Mute All Notifications</p>
              <p className="text-sm text-gray-500">Turn off all notifications at once</p>
            </div>
          </div>
          <button
            onClick={handleMuteAllToggle}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              muteAll ? 'bg-red-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                muteAll ? 'translate-x-6' : ''
              }`}
            />
          </button>
        </div>

        {/* Individual Toggles */}
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                muteAll ? 'opacity-50' : ''
              } ${notification.enabled ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  notification.enabled ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {notification.icon}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{notification.label}</p>
                  <p className="text-sm text-gray-500">{notification.description}</p>
                </div>
              </div>
              <button
                onClick={() => handleToggle(notification.id)}
                disabled={muteAll}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  notification.enabled ? 'bg-orange-500' : 'bg-gray-300'
                } ${muteAll ? 'cursor-not-allowed' : ''}`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    notification.enabled ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
